import {
  MAX_RENT_AMOUNT,
  MIN_RENT_AMOUNT,
  MOVE_IN_COSTS_PAYMENT_MIN_AMOUNT,
  ONE_TIME_PAYMENT_MIN_AMOUNT,
} from '@rental-manager/constants/PaymentConstants';

const errorMessages = {
  /* When an input is valid, set the error field to an empty string. */
  CLEAR_ERROR: '',

  /* Payment Settings Page */
  MONTHLY_RENT_EMPTY: 'Enter the monthly rent',
  MONTHLY_RENT_INVALID: `Monthly rent should be between $${(MIN_RENT_AMOUNT / 100).toLocaleString('en-US')} and $${(
    MAX_RENT_AMOUNT / 100
  ).toLocaleString('en-US')}`,

  ONBOARDING_V2_DUE_DATE_REQUIRED: 'Select the payment date',

  /* Account Info Page */
  ROUTING_NUMBER_EMPTY: 'Enter your routing number',
  ROUTING_NUMBER_INVALID: 'Routing number must be a 9-digit number',
  ROUTING_NUMBER_DEFAULT_ERROR: 'Enter a valid routing number',
  CHECKING_ACCOUNT_NUMBER_EMPTY: 'Enter your checking account number',
  CHECKING_ACCOUNT_NUMBER_INVALID: 'Account number must be a 12-digit number',
  CHECKING_ACCOUNT_NUMBER_CONFIRMATION_EMPTY: 'Confirm your checking account number',
  CHECKING_ACCOUNT_NUMBER_CONFIRMATION_NOT_MATCH: 'Account numbers don’t match',

  /* Identity Verification Page */
  LEGAL_FIRST_NAME_EMPTY: 'Enter your legal first name',
  LAST_NAME_EMPTY: 'Enter your last name',
  STREET_EMPTY: 'Enter your street address',
  CITY_EMPTY: 'Enter your address city',
  STATE_EMPTY: 'Select your address state',
  ZIP_CODE_EMPTY: 'Enter a 5-digit ZIP code',
  ZIP_CODE_INVALID: 'Enter a valid ZIP code',
  PHONE_NUMBER_INVALID: 'Enter a valid, 10-digit US phone number',
  MONTH_OF_BIRTH_EMPTY: 'Select the month of your birth',
  DAY_OF_BIRTH_EMPTY: 'Enter the day of your birth',
  DAY_OF_BIRTH_INVALID: 'Enter a valid day of birth',
  YEAR_OF_BIRTH_EMPTY: 'Enter the year of your birth',
  YEAR_OF_BIRTH_INVALID: 'Enter a valid year of birth',
  BIRTH_DATE_INVALID: 'You must be 18 years of age or older',
  EMAIL_ADDRESS_EMPTY: 'Enter your email address',
  INVALID_EMAIL_ADDRESS: 'Enter a valid email address',
  UNIQUE_EMAIL_ADDRESS: 'Tenant and landlord email addresses cannot match',
  CANNOT_INVITE_EXISTING_RENTER:
    'This tenant is already set up for online payments. To ' +
    'extend their rent term, edit the payment end date from your Payments dashboard.',
  BUSINESS_NAME_EMPTY: 'Enter your business name',
  EMPLOYER_IDENTIFICATION_NUMBER_INVALID: 'Enter a 9-digit number',
  BUSINESS_STREET_EMPTY: 'Enter your business street address',
  BUSINESS_CITY_EMPTY: 'Enter your business address city',
  BUSINESS_STATE_EMPTY: 'Select your business address state',
  FULL_SSN_INVALID: 'Enter a 9-digit number',

  /* Phone Verification Page */
  PHONE_VERIFICATION_INVALID: 'Enter a 5-digit verification code',
  PHONE_VERIFICATION_INCORRECT: 'The code is incorrect. Try again.',
  PHONE_VERIFICATION_TOO_MANY_ATTEMPTS: 'Too many attempts have been made. Contact support to continue.',

  /* Invite Tenant Page */
  TENANT_FIRST_NAME_EMPTY: 'Enter your tenant’s first name',
  TENANT_LAST_NAME_EMPTY: 'Enter your tenant’s last name',
  TENANT_EMAIL_ADDRESS_EMPTY: 'Enter your tenant’s email address',

  /* Invite Tenant Sign and collect variation */
  TENANT_NOT_SELECTED: 'Select your tenant',

  /* Edit Rent Amount Page */
  NEW_RENT_AMOUNT_MATCHES_PREVIOUS: 'The rent amount is already set to this value',

  /* One Time Payment Page */
  ONE_TIME_PAYMENT_NOTE_MAX_LENGTH: '45/45 — You’ve reached the maximum number of characters',
  ONE_TIME_PAYMENT_TYPE_EMPTY: 'Select what the payment is for',
  ONE_TIME_PAYMENT_TYPE_INVALID: 'This payment type is invalid',
  ONE_TIME_PAYMENT_AMOUNT_EMPTY: 'Enter the amount',
  ONE_TIME_PAYMENT_NOTES_EMPTY: 'Enter a note',
  ONE_TIME_PAYMENT_MAX_AMOUNT: (maxAmount) => `This amount can’t exceed ${maxAmount}`,
  ONE_TIME_PAYMENT_MIN_AMOUNT: `This amount can’t be less than $${ONE_TIME_PAYMENT_MIN_AMOUNT / 100}`,

  /* Move In Costs Page */
  MOVE_IN_COSTS_NOTE_MAX_LENGTH: '45/45 — You’ve reached the maximum number of characters',
  MOVE_IN_COSTS_FOR_EMPTY: 'Enter what this move-in cost is for',
  MOVE_IN_COSTS_PAYMENT_AMOUNT_EMPTY: 'Enter an amount',
  MOVE_IN_COSTS_PAYMENT_MAX_AMOUNT: (maxAmount) => `This amount can’t exceed ${maxAmount}`,
  MOVE_IN_COSTS_PAYMENT_MIN_AMOUNT: `This amount can’t be less than $${MOVE_IN_COSTS_PAYMENT_MIN_AMOUNT / 100}`,
  MOVE_IN_COSTS_TOTAL_LIMIT: (limit) => `The total of all costs can’t exceed ${limit}`,
  MOVE_IN_COSTS_DUE_DATE_EMPTY: 'Select a due date',
  MOVE_IN_COSTS_BEFORE_START_DATE: 'Select a future date',
  MOVE_IN_COSTS_AFTER_CUTOFF_DATE: (cutoffDate) => `Select a date before ${cutoffDate}`,

  /* Renter Payment info */
  PAYMENT_CARD_NAME_EMPTY: 'Enter the name that appears on your card',
  PAYMENT_BILLING_STREET_ADDRESS: 'Enter your billing address',
  PAYMENT_ADDRESS_CITY_EMPTY: 'Enter your city',
  PAYMENT_STATE_EMPTY: 'Select the state for your billing address',
  PAYMENT_ZIP_CODE_EMPTY: 'Enter your ZIP code',
  PAYMENT_INVALID_TYPE_CARD: 'We only accept Visa, MasterCard, American Express, and Discover cards',
  PAYMENT_TYPE_NOT_VALID: 'At this time we only accept debit and credit cards. We don’t accept prepaid or other cards.',
  PAYMENT_TYPE_MISMATCH: (cardType) => `The card information you have entered is not a ${cardType} card`,

  /* Payment Plan Installment Requests Page */
  PAYMENT_PLAN_NOTE_MAX_LENGTH: '45/45 — You’ve reached the maximum number of characters',
  PAYMENT_PLAN_AMOUNT_EMPTY: 'Enter the amount',
  PAYMENT_PLAN_NOTES_EMPTY: 'Enter a note',
  PAYMENT_PLAN_MAX_AMOUNT: (maxAmount) => `This amount can’t exceed ${maxAmount}`,
  PAYMENT_PLAN_MIN_AMOUNT: (minAmount) => `This amount can’t be less than ${minAmount}`,

  /* Rent reporting */
  COMBINED_NAMES_ERROR_TEXT: 'Multiple names detected. Enter only one name for your primary tenant.',
  COMBINED_NAMES_ERROR_TEXT_CONFIRM:
    'Multiple names detected. Review and correct the name your landlord provided if needed.',
  COMBINED_NAMES_ERROR_TEXT_RENTER: 'Multiple names detected. Enter only one name.',
};

export default errorMessages;
